import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NovaCoin3D } from './NovaCoin3D.js';

interface StreamCoin {
  id: number;
  startX: number;
  startY: number;
  midX: number;
  midY: number;
  targetX: number;
  targetY: number;
  scale: number;
  delay: number;
  rotation: number;
}

interface CoinStreamEffectProps {
  triggerKey: number; // Incrementing counter to trigger stream
}

export const CoinStreamEffect: React.FC<CoinStreamEffectProps> = ({ triggerKey }) => {
  const [coins, setCoins] = useState<StreamCoin[]>([]);
  const [showDestinationGlow, setShowDestinationGlow] = useState(false);

  useEffect(() => {
    if (triggerKey <= 0) return;

    // Check prefers-reduced-motion
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      return;
    }

    const count = 9; // Controlled, high-efficiency particle pool
    const newCoins: StreamCoin[] = [];

    for (let i = 0; i < count; i++) {
      // Origin: reactor chamber (centered, slightly lower)
      const startX = (Math.random() - 0.5) * 60;
      const startY = 60 + (Math.random() - 0.5) * 30;

      // Arc apex: swooping slightly outward before converging
      const curveDir = i % 2 === 0 ? 1 : -1;
      const midX = startX + curveDir * (30 + Math.random() * 40);
      const midY = -80 - Math.random() * 50;

      // Destination: top header balance pill area
      const targetX = (Math.random() - 0.5) * 36;
      const targetY = -270 - Math.random() * 30;

      newCoins.push({
        id: Date.now() + i,
        startX,
        startY,
        midX,
        midY,
        targetX,
        targetY,
        scale: 0.65 + Math.random() * 0.35,
        delay: i * 0.07, // Staggered stream
        rotation: (Math.random() - 0.5) * 360,
      });
    }

    setCoins(newCoins);

    // Destination impact highlight triggers when first coins arrive
    const destTimer = setTimeout(() => {
      setShowDestinationGlow(true);
    }, 420);

    const destEndTimer = setTimeout(() => {
      setShowDestinationGlow(false);
    }, 1250);

    // Complete cleanup of DOM and animation state
    const cleanTimer = setTimeout(() => {
      setCoins([]);
      setShowDestinationGlow(false);
    }, 1500);

    return () => {
      clearTimeout(destTimer);
      clearTimeout(destEndTimer);
      clearTimeout(cleanTimer);
    };
  }, [triggerKey]);

  if (coins.length === 0 && !showDestinationGlow) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center overflow-hidden"
      aria-hidden="true"
    >
      {/* Destination Balance Indicator Golden Flare */}
      <AnimatePresence>
        {showDestinationGlow && (
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1.25 }}
            exit={{ opacity: 0, scale: 1.8 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            className="absolute -translate-y-[280px] w-28 h-12 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse, rgba(254, 240, 138, 0.7) 0%, rgba(245, 158, 11, 0.4) 45%, transparent 75%)',
              filter: 'blur(8px)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Streaming 3D Micro-Coins */}
      <AnimatePresence>
        {coins.map((c) => (
          <motion.div
            key={c.id}
            initial={{
              opacity: 0,
              x: c.startX,
              y: c.startY,
              scale: 0.25,
              rotate: 0,
            }}
            animate={{
              opacity: [0, 1, 1, 0],
              x: [c.startX, c.midX, c.targetX],
              y: [c.startY, c.midY, c.targetY],
              scale: [0.25, c.scale, 0.35],
              rotate: c.rotation,
            }}
            transition={{
              duration: 0.95,
              delay: c.delay,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute drop-shadow-[0_0_10px_rgba(245,158,11,0.7)]"
            style={{ willChange: 'transform, opacity' }}
          >
            <NovaCoin3D size="xs" animate={false} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
